"use client";

import { Search, X } from "lucide-react";
import { useTranslations } from "next-intl";
import clsx from "clsx";
import LottieLoader from "@/components/LottieLoader";

type SearchInputProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
  /** Shows a spinner in place of the clear button while a query is in flight. */
  isLoading?: boolean;
  /**
   * Shown under the field while the typed value is too short to search on.
   * Omit when the caller searches on every keystroke.
   */
  hint?: string;
};

/**
 * Name filter for the public web grids.
 *
 * Padding is logical (`ps`/`pe`) rather than left/right so the icon and the
 * trailing control stay on the correct sides in Arabic.
 *
 * The spinner replaces the clear button rather than sitting beside it: the two
 * would otherwise fight for the same slot, and a query in flight is exactly
 * when the field should not look idle.
 */
export default function SearchInput({
  value,
  onChange,
  placeholder,
  className,
  isLoading = false,
  hint,
}: SearchInputProps) {
  const t = useTranslations("web.pagination");

  return (
    <div className={className}>
      <div className="relative">
        <Search
          size={18}
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 start-4 -translate-y-1/2 text-secondary"
        />
        <input
          type="search"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-busy={isLoading}
          className="w-full rounded-[10px] border border-slate-200 bg-white py-3 ps-12 pe-12 text-sm font-medium text-primary shadow-sm outline-none transition-colors placeholder:font-normal placeholder:text-slate-400 focus:border-secondary focus:ring-2 focus:ring-secondary/20 [&::-webkit-search-cancel-button]:hidden"
        />

        {isLoading ? (
          <span className="absolute top-1/2 end-3 flex -translate-y-1/2 items-center">
            <LottieLoader size={28} label={t("searching")} />
          </span>
        ) : value ? (
          <button
            type="button"
            onClick={() => onChange("")}
            aria-label={t("clear_search")}
            className="absolute top-1/2 end-4 flex h-6 w-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-[10px] text-slate-400 transition-colors hover:bg-slate-100 hover:text-primary"
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      {hint ? (
        <p
          // Announced politely: it appears mid-typing, and an assertive live
          // region would interrupt the user on every keystroke.
          aria-live="polite"
          className={clsx("mt-2 text-xs font-medium text-slate-400", "px-1")}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}
